import { and, eq, or } from "drizzle-orm";
import { redirect } from "react-router";
import { formatDate } from "~/components/site/Date";
import { z } from "zod";
import { PencilIcon } from "~/components/site/icons/Pencil";
import { LinkButton } from "~/components/site/links/LinkButton";
import { MdContent } from "~/components/site/MdContent";
import { UserLink } from "~/components/site/UserLink";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { Facts } from "~/components/ui/Facts";
import { InfoBox } from "~/components/ui/InfoBox";
import { PageTitle } from "~/components/ui/PageTitle";
import { pg } from "~/pg.server";
import { tt, useT, useTurl } from "~/util/Lang";
import { $LOADER, $PAGE } from "~/util/Loaders";
import { loadMarkdown } from "~/util/Markdown.server";
import { logPageView } from "~/util/stats";
import { getTitle } from "~/util/wiki";

const Params = z.object({ userId: z.string(), pageId: z.string() });

$PAGE({
  path: "users/:userId/pages/:pageId",
  loader: $LOADER?.(async (cx) => {
    const t = tt.bind(null, cx.realm.lang);
    const params = Params.parse(cx.params);
    const viewerId = cx.req.sess?.user?.id ?? 0;

    const pageMeta = await pg.db
      .select({
        pageId: pg.mdPages.id,
        draftUserId: pg.userPages.userId,
        draftPublic: pg.userPages.public,
      })
      .from(pg.mdPages)
      .innerJoin(pg.userPages, eq(pg.userPages.id, pg.mdPages.draftId))
      .where(
        and(
          eq(pg.mdPages.id, parseInt(params.pageId)),
          eq(pg.mdPages.isDeleted, false)
        )
      )
      .limit(1)
      .then((res) => res[0]);

    if (!pageMeta) {
      throw Response.json(
        { msg: t({ en: "Page not found.", ru: "Страница не найдена." }) },
        { status: 404 }
      );
    }

    // Log
    logPageView(cx);
    //

    const isOwner = pageMeta.draftUserId === viewerId;
    if (!pageMeta.draftPublic && !isOwner) {
      return redirect("/login");
    }

    const user = await pg.db
      .select({ id: pg.users.id, login: pg.users.login })
      .from(pg.users)
      .where(eq(pg.users.id, parseInt(params.userId)))
      .limit(1)
      .then((res) => res[0]);
    if (!user) {
      return redirect("/");
    }

    const page = await pg.db
      .select({
        id: pg.mdPages.id,
        slug: pg.mdPages.wikiSlug,
        game: pg.mdPages.game,
        public: pg.userPages.public,
        createdAt: pg.mdPages.createdAt,
        revision: {
          titleEn: pg.mdRevisions.titleEn,
          contentEn: pg.mdRevisions.contentEn,
          titleRu: pg.mdRevisions.titleRu,
          contentRu: pg.mdRevisions.contentRu,
          createdAt: pg.mdRevisions.createdAt,
        },
        draft: {
          userId: pg.userPages.userId,
          public: pg.userPages.public,
        },
        user: {
          id: pg.users.id,
          name: pg.users.login,
          avatar: pg.users.avatar,
        },
      })
      .from(pg.mdPages)
      .where(eq(pg.mdPages.id, parseInt(params.pageId)))
      .innerJoin(pg.userPages, eq(pg.userPages.id, pg.mdPages.draftId))
      .innerJoin(pg.mdRevisions, eq(pg.mdRevisions.id, pg.mdPages.revisionId))
      .innerJoin(pg.users, eq(pg.users.id, pg.userPages.userId))
      .limit(1)
      .then((res) => res[0]);

    const content =
      page?.revision.contentEn && cx.realm.lang === "en"
        ? await loadMarkdown(
            page.revision.contentEn ?? "",
            cx.gd.all_gd!,
            cx.realm.lang,
            cx.realm.game
          )
        : page?.revision.contentRu &&
          (await loadMarkdown(
            page.revision.contentRu ?? "",
            cx.gd.all_gd!,
            cx.realm.lang,
            cx.realm.game
          ));

    return {
      authUser: cx.req.sess?.user,
      user,
      page: {
        id: page.id,
        title: getTitle(page.revision.titleEn, page.revision.titleRu),
        user: page.user,
        createdAt: page.createdAt,
        updatedAt: page.revision.createdAt,
        public: page.draft.public,
        gver: page.game,
        content,
      },
    };
  }),

  meta: (args) => {
    return {
      title: `${args.t(args.data.page.title)} - ${args.t({
        en: "Pages",
        ru: "Страницы",
      })} - ${args.data.user.login} - ${args.t({
        en: "Users",
        ru: "Пользователи",
      })} - L2Hub.info`,
      canonicalPath: `/users/${args.data.user.id}/pages/${args.data.page.id}`,
    };
  },

  render: ({ data }) => {
    const { user, authUser, page } = data;
    const t = useT();
    const turl = useTurl();
    const pageTitle = t(page.title);
    const breadcrumbList = [
      { title: t({ en: "Users", ru: "Пользователи" }), url: turl("/users") },
      {
        title: user.login,
        url: turl(`/users/${user.id}`),
      },
      {
        title: t({ en: "Pages", ru: "Страницы" }),
        url: turl(`/users/${user.id}/pages`),
      },
      {
        title: pageTitle,
        url: turl(`/users/${user.id}/pages/${page.id}`),
      },
    ];

    return (
      <div className="content">
        <Breadcrumb noRoot={true} items={breadcrumbList} />
        <div className="columns">
          <div className="column is-three-quarters">
            <PageTitle>{pageTitle}</PageTitle>
            {page.content && <MdContent>{page.content}</MdContent>}
          </div>
          <div className="column">
            <InfoBox
              title={t({
                en: "Information",
                ru: "Краткая информация",
              })}
            >
              <Facts
                facts={[
                  {
                    name: t({ en: "Author", ru: "Автор" }),
                    value: <UserLink user={page.user} />,
                  },
                  {
                    name: t({ en: "Created at", ru: "Создано" }),
                    value: formatDate(page.createdAt),
                  },
                  {
                    name: t({ en: "Updated at", ru: "Обновлено" }),
                    value: formatDate(page.updatedAt),
                  },
                  {
                    name: t({ en: "Gver", ru: "Gver" }),
                    value:
                      page.gver && page.gver?.length > 0
                        ? page.gver.join(", ")
                        : "All",
                  },
                  {
                    name: t({ en: "Access", ru: "Доступ" }),
                    value: page.public ? "Public" : "Private",
                  },
                ]}
              />
              {page.user.id === authUser?.id && (
                <div style={{ marginTop: 15 }}>
                  <LinkButton
                    to={turl(`/users/${user.id}/pages/${page.id}/edit`)}
                  >
                    <PencilIcon width={20} height={20} />
                    {t({ en: "Edit Page", ru: "Редактирвоать" })}
                  </LinkButton>
                </div>
              )}
            </InfoBox>
          </div>
        </div>
      </div>
    );
  },
});
